<template>
    <div id="container">
        <h3 class="container-tit">은평생활지도</h3>
        <div class="business-map">
            <!-- 지도 -->
            <DrawRegionNaverMap />
            <!-- 선택 패널 -->
            <LifemapSelectPane id="map-category-pop" />
            <!-- 범례열기 -->
            <div class="map_legend2_open"><a href="#" title="범례열기">범례</a></div>
            <!-- 범례 -->
            <LifemapLegend :life-population-hour="lifePopulationHour"
                @change-life-population-hour="changeLifePopulationHourHandler"/>
        </div>
        <button type="button" class="button-change m-visible" @click="toggleLifemapSelectPane">{{ selectPaneBtnTxt }}</button>

        <FacilityInfoWin v-show="false"
            v-on:loaded-facility-info="loadedFacilityInfoHandler"
            ref="facilityInfoWin"/>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import NaverMapMixin from "@/store/naver.map.local.mixin.js";
import { $WC } from '@/conf/common.const.js';
import gripUtil from '@/lib/grip.util.js';
import DrawRegionNaverMap from "@/components/DrawRegionNaverMap.vue";
import LifemapSelectPane from "@/views/lifemap/LifemapSelectPane.vue";
import LifemapLegend from "@/views/lifemap/LifemapLegend.vue";
import FacilityInfoWin from "@/views/lifemap/FacilityInfoWin.vue";

export default {
    name: 'Lifemap',
    data() {
        return {
            // 주민등록 인구 데이터 로딩 체크
            isLoadedRegPopulationData: false,
            // 생활 인구 데이터 로딩 체크
            isLoadedLifePopulationData: false,
            // 생활인구 시간
            lifePopulationHour: '14',
            selectPaneBtnTxt: '변경하기',
        };
    },
    computed: {
        ...mapGetters({
            selectedTopTab: 'getSelectedTopTab',
            selectedMidTab: 'getSelectedMidTab',
            selectedFacSqnos: 'getSelectedFacSqnos',
            hjdInfoList: 'getHjdInfoList',
            facDataMap: 'getFacDataMap',
            facColMap: 'getFacColMap',
            hjdPopulationList: 'getHjdPopulationList',
            hjdDisaledPopulationList: 'getHjdDisaledPopulationList',
            hjdPensionerPopulationList: 'getHjdPensionerPopulationList',
            localLifePopulationMap: 'getLocalLifePopulationMap',
            shrotStayPopulationMap: 'getShrotStayPopulationMap',
            longStayPopulationMap: 'getLongStayPopulationMap',
        }),
    },
    watch: {
        selectedFacSqnos: {
            immediate: false,
            handler(newVal, oldVal) {
                this.placeMarkers();
            }
        },
        selectedTopTab: {
            immediate: false,
            handler(newVal, oldVal) {
                this.lifePopulationHour = "14";
            }
        },
        selectedMidTab: {
            immediate: false,
            handler(newVal, oldVal) {
                this.fillColorPopulation();
            }
        }
    },
    created() {
        this.$eventBus.$once('NaverMap::map-loaded', map => {
            this.naverMaps = map;
        });

        this.$eventBus.$once("DrawRegionNaverMap::completed-draw-region", geoJsonDrawing => {
            this.geoJsonDrawing = geoJsonDrawing;
            this.placeMarkers();
        });

        // 주민등록 인구 정보 데이터 로딩 완료 시
        this.$eventBus.$once('LifemapApp::loaded-reg-population-data', () => {
            this.isLoadedRegPopulationData = true;
            this.fillColorPopulation();
        });

        // 샐활 인구 정보 데이터 로딩 완료 시
        this.$eventBus.$once('LifemapApp::loaded-reg-population-data', () => {
            this.isLoadedRegPopulationData = true;
        });
    },
    methods: {
        placeMarkers() {
            if(typeof this.geoJsonDrawing !== $WC.UNDEF && this.facDataMap.dataLoaded) {
                // 모든 마커 제거
                this.disableAllMarker();
                this.selectedFacSqnos.forEach((sqno => {
                    let facDataList = this.facDataMap[sqno];
                    let markerGroupKey = "fac_"  + sqno;
                    if(!this.hasMarkers(markerGroupKey)) {
                        facDataList.forEach(facData => {
                            if(facData.lttdVal && facData.lgtdVal) {
                                this.makeMarker(markerGroupKey, facData, (event, facData, marker) => {
                                    let facCols = this.facColMap[facData.dataKdSqno];
                                    this.$refs.facilityInfoWin.setFacData(facData, facCols);
                                }, 'marker' + facData.dataKdSqno + '.png', 'images/marker2/', 38, 38);
                            }
                        });
                    }
                    this.showMarkers(markerGroupKey, true);
                }));
            }
        },
        // 시설정보 팝업
        loadedFacilityInfoHandler(content) {
            this.openInfoWin(content);
        },
        // 인구 색상 표시
        fillColorPopulation() {
            if(typeof this.geoJsonDrawing !== $WC.UNDEF && this.isLoadedRegPopulationData === true) {
                let selectedTopTabCode = this.selectedTopTab.code;
                if(selectedTopTabCode === 'age' || selectedTopTabCode === 'vulnerable') {
                    this.regPopulation();
                } else if(selectedTopTabCode === 'local' || selectedTopTabCode === 'foreigner') {
                    this.lifePopulation();
                }
            }
        },
        // 주민등록 인구 지도 표기
        regPopulation() {
            let selectedMidTab = this.selectedMidTab;
            let selectedTopTabCode = this.selectedTopTab.code;
            let selectedMidTabCode = selectedMidTab.code;
            let popList = [];
            // 연령별 인구
            if(selectedTopTabCode === 'age') {
                popList = this.hjdPopulationList;
            }
            // 장애인 운기
            else if(selectedMidTabCode === 'disabledPersonPop') {
                popList = this.hjdDisaledPopulationList;
            }
            // 기초수급자 인구
            else if(selectedMidTabCode === 'pensionerPop') {
                popList = this.hjdPensionerPopulationList;
            }

            let colorScale = selectedMidTab.colorScale;
            let popCols = selectedMidTab.popCols;
            popList.forEach(pop => {
                let total = 0;
                popCols.forEach((popCol) => {
                    total += Number(pop[popCol]);
                });
                let color = colorScale(total);
                this.geoJsonDrawing.setFillColor(pop[selectedMidTab.hjdCodeCol], color);
            });
        },
        // 생활지도 인구 지도 표시
        lifePopulation() {
            let selectedMidTab = this.selectedMidTab;
            let selectedTopTabCode = this.selectedTopTab.code;
            let selectedMidTabCode = selectedMidTab.code;
            let hour = this.lifePopulationHour;
            let popList = [];

            if(selectedTopTabCode === 'local') {
                popList = this.localLifePopulationMap[hour];
            } else if(selectedMidTabCode === 'totalForeignerPop') {
                let longStayPopulationList = this.longStayPopulationMap[hour];
                let shrotStayPopulationList = this.shrotStayPopulationMap[hour];

                let totalForeignerPopMap = {};
                longStayPopulationList.forEach((longStayPop) => {
                    totalForeignerPopMap[longStayPop.val1] = {};
                    totalForeignerPopMap[longStayPop.val1].hjdCode = longStayPop.val1;
                    totalForeignerPopMap[longStayPop.val1].popCnt = Number(longStayPop.val4);
                });

                shrotStayPopulationList.forEach((shortStayPop) => {
                    let totalForeignerPop = totalForeignerPopMap[shortStayPop.val1];
                    if(typeof totalForeignerPop !== $WC.UNDF) {
                        totalForeignerPop.popCnt += Number(shortStayPop.val4);
                    }
                });

                popList = gripUtil.toList(totalForeignerPopMap);
            } else if(selectedMidTabCode === 'stopoverPop') {
                popList = this.shrotStayPopulationMap[hour];
            } else if(selectedMidTabCode === 'longStayPop') {
                popList = this.longStayPopulationMap[hour];
            }


            let colorScale = selectedMidTab.colorScale;
            let popCols = selectedMidTab.popCols;
            popList.forEach(pop => {
                let total = 0;
                popCols.forEach(function(popCol) {
                    total += Number(pop[popCol]);
                });
                let color = colorScale(total);
                let hjdCode = pop[selectedMidTab.hjdCodeCol];
                if(hjdCode.length === 8) {
                    hjdCode += "00";
                }
                this.geoJsonDrawing.setFillColor(hjdCode, color);
            });
        },
        // 생활인구 시간 변경 시
        changeLifePopulationHourHandler(hour) {
            this.lifePopulationHour = hour;
            this.lifePopulation();
        },
        toggleLifemapSelectPane() {
            let lifemapSelectPaneDisplay = document.getElementById('map-category-pop').style.display;

            if(lifemapSelectPaneDisplay === '' || lifemapSelectPaneDisplay === 'none') {
                document.getElementById('map-category-pop').style.display = 'block';
                this.selectPaneBtnTxt = "변경완료";
            } else {
                document.getElementById('map-category-pop').style.display = 'none';
                this.selectPaneBtnTxt = "변경하기";
            }
        },
    },
    components: {
        DrawRegionNaverMap,
        LifemapSelectPane,
        LifemapLegend,
        FacilityInfoWin
    },
    mixins: [NaverMapMixin],
}
</script>
